import { useEffect, useState } from "react";
import { Avatar, Button, DateTimePicker, Input, Label, Modal, ModalFooter, Select, Textarea, useConfirm, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import type { Task } from "@/lib/types";

/**
 * Unified create + edit dialog for tasks. `target` controls the mode:
 *   - "new"  → fresh row (with optional `defaultContactId` to pre-link).
 *   - Task   → edit existing row; PATCHes on save and supports delete.
 *
 * When the host hasn't supplied a contact (e.g. the global /tasks page in
 * create mode), the dialog renders an inline contact search so the user
 * can link the task. When the host has supplied one (a contact's panel),
 * the picker is hidden because the task is implicitly scoped.
 */
export function TaskDialog({
  target, defaultContactId, onClose, onSaved, onDeleted,
}: {
  target: Task | "new" | null;
  defaultContactId?: string | null;
  onClose: () => void;
  onSaved: (task: Task, mode: "create" | "edit") => void;
  onDeleted?: (task: Task) => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const open = target !== null;
  const editing = target && target !== "new" ? target : null;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState("normal");
  const [status, setStatus] = useState<"pending" | "completed">("pending");
  const [contactId, setContactId] = useState<string | null>(null);
  const [contactQuery, setContactQuery] = useState("");
  const [contactOptions, setContactOptions] = useState<{ id: string; full_name: string | null; email: string | null }[]>([]);
  const [saving, setSaving] = useState(false);

  // Reset whenever the dialog opens for a different target. The
  // datetime-local input wants `YYYY-MM-DDTHH:mm`, so we slice ISO strings.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTitle(editing.title);
      setDescription(editing.description ?? "");
      setDue(editing.due_at ? editing.due_at.slice(0, 16) : "");
      setPriority(editing.priority);
      setStatus(editing.status === "completed" ? "completed" : "pending");
      setContactId(editing.contact_id);
      setContactQuery(editing.contact_name ?? "");
    } else {
      setTitle(""); setDescription(""); setDue(""); setPriority("normal"); setStatus("pending");
      setContactId(defaultContactId ?? null);
      setContactQuery("");
    }
    setContactOptions([]);
  }, [open, editing, defaultContactId]);

  // Debounced contact search — only relevant when the host hasn't pinned
  // a contact AND the user is typing into the picker.
  useEffect(() => {
    if (!open || defaultContactId) return;
    const t = setTimeout(() => {
      if (!contactQuery.trim() || contactId) { setContactOptions([]); return; }
      api.get<{ contacts: { id: string; full_name: string | null; email: string | null }[] }>("/contacts", { q: contactQuery, limit: 8 })
        .then((d) => setContactOptions(d.contacts))
        .catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [contactQuery, contactId, open, defaultContactId]);

  const submit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        const r = await api.patch<{ task: Task }>(`/tasks/${editing.id}`, {
          title: title.trim(),
          description: description.trim() || null,
          due_at: due || null,
          priority,
          status,
          contact_id: contactId,
        });
        toast.success("Task updated");
        onSaved(r.task, "edit");
      } else {
        const r = await api.post<{ task: Task }>("/tasks", {
          title: title.trim(),
          description: description.trim() || null,
          due_at: due || null,
          priority,
          contact_id: contactId,
        });
        toast.success("Task created");
        onSaved(r.task, "create");
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  const onDelete = async () => {
    if (!editing) return;
    if (!(await confirm({
      title: `Delete "${editing.title}"?`,
      confirmLabel: "Delete task",
      danger: true,
    }))) return;
    try {
      await api.delete(`/tasks/${editing.id}`);
      toast.success("Task deleted");
      onDeleted?.(editing);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    }
  };

  // The host pins the contact via `defaultContactId`; in those cases we
  // hide the picker since changing the contact would defy the host's
  // scope. Globally-created tasks (the /tasks page) get the picker.
  const showContactPicker = !defaultContactId;

  return (
    <Modal open={open} onClose={onClose} title={editing ? "Edit task" : "New task"} size="md">
      <div className="space-y-3">
        <div>
          <Label required>What needs to be done?</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Call John about renewal" autoFocus />
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="min-h-16 text-xs" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Due</Label><DateTimePicker value={due} onChange={setDue} placeholder="Pick a due date" /></div>
          <div>
            <Label>Priority</Label>
            <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </Select>
          </div>
        </div>
        {editing && (
          <div>
            <Label>Status</Label>
            <Select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
            </Select>
          </div>
        )}
        {showContactPicker && (
          <div>
            <Label>Linked contact (optional)</Label>
            <Input
              value={contactQuery}
              onChange={(e) => { setContactQuery(e.target.value); setContactId(null); }}
              placeholder="Search contact…"
            />
            {contactOptions.length > 0 && (
              <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-zinc-800 bg-zinc-950">
                {contactOptions.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setContactId(c.id); setContactQuery(c.full_name ?? c.email ?? ""); setContactOptions([]); }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-900"
                  >
                    <Avatar name={c.full_name ?? c.email ?? "?"} size="xs" />
                    <span className="truncate text-zinc-200">{c.full_name ?? c.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <ModalFooter>
        {editing && onDeleted && (
          <Button variant="ghost" onClick={onDelete} className="mr-auto text-rose-400 hover:bg-rose-500/10">
            Delete
          </Button>
        )}
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={!title.trim()}>
          {editing ? "Save changes" : "Create"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
